import { EffectRef, Injectable, Injector, computed, effect, inject, runInInjectionContext, signal } from '@angular/core';
import { escapeRegExp, uniq } from 'lodash';
import { ParamRegistry } from './param-registry';
import { TranslationManager, TranslationType, Translator } from '../../util/translation-manager';
import { ScalarParam } from '../core/scalar-param';
import { ParamTranslationDef } from '../util/param-system-def';

class SingleTranslator implements Translator {
    private regex: RegExp;
    private reverseReplacement: string;
    private replacement = signal<string>('');
    private reverseRegex = computed(() => new RegExp(escapeRegExp(this.replacement())));

    constructor(
        key: string,
        private def: ParamTranslationDef,
    ) {
        this.reverseReplacement = `{{${key}}}`;
        this.regex = new RegExp(this.reverseReplacement);
        this.replacement.set(this.def.default);
    }

    update(value: number) {
        const replacement = this.def.map[value];
        const newReplacement = typeof replacement === 'string' ? replacement : this.def.default;
        if (this.replacement() !== newReplacement) {
            this.replacement.set(newReplacement);
        }
    }

    translate(str: string, _type: TranslationType) {
        const rep = this.replacement(); // register signal dependency
        if (this.regex.exec(str)) {
            return str.replace(this.regex, rep);
        } else {
            return str;
        }
    }

    cislate(str: string, _type: TranslationType) {
        const revRegex = this.reverseRegex(); // register signal dependency
        if (revRegex.exec(str)) {
            return str.replace(revRegex, this.reverseReplacement);
        } else {
            return str;
        }
    }
}

class SingleParamTranslator implements Translator {
    private effectRef: EffectRef;
    private translators: SingleTranslator[];

    constructor(
        reg: ParamRegistry,
        injector: Injector,
        private defs: { [key: string]: ParamTranslationDef },
    ) {
        const defKeys = Object.keys(defs);
        const defsArray = defKeys.map((key) => defs[key]);
        if (defsArray.some((def) => def.param !== defsArray[0].param)) {
            throw new Error('API violation');
        }
        const param = reg.typedParamByKeyThrow(ScalarParam, defsArray[0].param);
        reg.registerEarlyLoadParam(param);

        this.translators = defKeys.map((key) => new SingleTranslator(key, this.defs[key]));
        this.effectRef = runInInjectionContext(injector, () =>
            effect(() => {
                const value = param.valueSignal();
                if (value !== undefined) {
                    this.onValueChange(value);
                }
            }),
        );
    }

    translate(str: string, type: TranslationType) {
        let out = str;
        for (const translator of this.translators) {
            out = translator.translate(out, type);
        }
        return out;
    }

    cislate(str: string, type: TranslationType) {
        let out = str;
        for (const translator of this.translators) {
            out = translator.cislate(out, type);
        }
        return out;
    }

    destroy() {
        this.effectRef.destroy();
    }

    private onValueChange = (value: number) => {
        for (const translator of this.translators) {
            translator.update(value);
        }
    };
}

@Injectable({ providedIn: 'root' })
export class ParamKeyedTranslator implements Translator {
    private translators: SingleParamTranslator[] = [];
    private injector = inject(Injector);

    constructor(
        reg: ParamRegistry,
        private translationManager: TranslationManager,
    ) {
        this.translationManager.add(this);
        reg.ready().subscribe((rec) => {
            for (const translator of this.translators) {
                translator.destroy();
            }
            const def = rec.def.paramTranslations || {};
            const params = Object.keys(def).map((key) => def[key].param);
            this.translators = uniq(params).map((param) => {
                const translationKeys = Object.keys(def).filter((key) => def[key].param === param);
                const defs: { [key: string]: ParamTranslationDef } = {};
                for (const translationKey of translationKeys) {
                    defs[translationKey] = def[translationKey];
                }
                return new SingleParamTranslator(reg, this.injector, defs);
            });
        });
    }

    translate(str: string, type: TranslationType) {
        let out = str;
        for (const translator of this.translators) {
            out = translator.translate(out, type);
        }
        return out;
    }

    cislate(str: string, type: TranslationType) {
        let out = str;
        for (const translator of this.translators) {
            out = translator.cislate(out, type);
        }
        return out;
    }
}
